/**
 * Current Campaigns API client — talks to /api/ad-ops/current-campaigns, the
 * live GAM snapshot behind the Ad Ops dashboard's Current Campaigns tab.
 * Types mirror backend/src/services/gamCurrentCampaignsService.ts.
 */

import { apiRequest } from './api';

const BASE = '/api/ad-ops/current-campaigns';

export type GamPacingStatus = 'ON_TRACK' | 'AHEAD' | 'BEHIND' | 'NOT_STARTED' | 'COMPLETED';

export interface CampaignChannel {
  channelName: string;
  channelId: string | null;
  impressions: number;
}

export interface CurrentCampaign {
  ioOrderNumber: string | null;
  gamOrderId: string;
  gamOrderName: string;
  startDate: string;
  endDate: string;
  lineItemCount: number;
  totalImpressionsGoal: number;
  /** Goal the pacing bar uses — the booked total minus any DEACTIVATED
   *  lines' units. Absent on snapshots written before Sept 2026. */
  pacingImpressionsGoal?: number;
  totalImpressionsDelivered: number;
  deliveryPercent: number;
  flightProgressPercent: number;
  /** GAM DeliveryIndicator rollup (falls back to flightProgressPercent when
   *  absent — snapshots written before Aug 2026). */
  expectedDeliveryPercent?: number;
  pacingStatus: GamPacingStatus;
  /** Lines whose goal is a sponsorship "Impression Limit" (GAM gives a
   *  sponsorship a % goal; the limit is its booked quantity, and it is included
   *  in totalImpressionsGoal). Absent on snapshots written before Sept 2026. */
  impressionLimitLineItems?: number;
  /** Sponsorship lines with NO Impression Limit — 100 % SOV, unpaced, goal 0. */
  unlimitedSponsorshipLineItems?: number;
  windowImpressions: number;
  unattributedImpressions: number;
  channels: CampaignChannel[];
  channelCountTotal: number;
  channelsTruncated: boolean;
  otherChannelsImpressions: number;
}

export interface CurrentCampaignsBrand {
  advertiserId: string;
  advertiserName: string;
  advertiserNameResolved: boolean;
  campaignCount: number;
  windowImpressions: number;
  channels: CampaignChannel[];
  channelCountTotal: number;
  channelsTruncated: boolean;
  otherChannelsImpressions: number;
  campaigns: CurrentCampaign[];
}

export interface CurrentCampaignsSnapshot {
  status: 'refreshing' | 'complete' | 'error';
  fetchedAt?: string;
  refreshStartedAt?: string;
  refreshFinishedAt?: string;
  requestedBy?: string | null;
  windowStart?: string;
  windowEnd?: string;
  brands?: CurrentCampaignsBrand[];
  totals?: {
    brandCount: number;
    campaignCount: number;
    channelCount: number;
    windowImpressions: number;
  };
  channelReportError?: string;
  error?: string;
}

/** One brand's delivery on one channel (channel-search index row). */
export interface ChannelIndexBrand {
  advertiserId: string;
  advertiserName: string;
  impressions: number;
  campaignCount: number;
}

/** One channel's full brand roster — the channel-first inverse of the brand cards. */
export interface ChannelIndexEntry {
  channelName: string;
  channelId: string | null;
  impressions: number;
  campaignCount: number;
  brandCount: number;
  brands: ChannelIndexBrand[];
  brandsTruncated: boolean;
  otherBrandsImpressions: number;
}

/**
 * Channel-search index from the same refresh as the snapshot. Unlike the
 * brand cards' truncated channel lists, this covers EVERY channel with window
 * delivery — the authoritative "which brands are on channel X, if any".
 */
export interface CurrentCampaignsChannelIndex {
  fetchedAt?: string;
  windowStart?: string;
  windowEnd?: string;
  channelCount: number;
  entries: ChannelIndexEntry[];
  entriesTruncated: number;
}

/** Cached snapshot; the backend kicks a detached refresh itself when it's >24h stale. */
export async function getCurrentCampaigns(): Promise<{
  snapshot: CurrentCampaignsSnapshot | null;
  channelIndex: CurrentCampaignsChannelIndex | null;
  refreshInFlight: boolean;
}> {
  return apiRequest(BASE);
}

/** Manual refresh — 202 fire-and-forget; poll getCurrentCampaigns for the result. */
export async function refreshCurrentCampaigns(): Promise<{ status: string }> {
  return apiRequest(`${BASE}/refresh`, { method: 'POST' });
}
